import axios, { AxiosError } from 'axios';
import type {
  Project,
  ProjectCreateInput,
  Document,
  Annotation,
  DocumentAnnotationsSummary,
  Template,
  GenerateDocumentRequest,
  GenerateDocumentResponse,
  ProcessingStats,
  AppSettings,
  APIError,
} from '@/types';

// Detect environment and set API base URL
// When on port 8080 we're behind Docker nginx - use relative URL so /api is proxied to backend
const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const isDockerDev = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && window.location.port === '8080';
const API_BASE_URL = window.electronAPI?.isElectron
  ? 'http://127.0.0.1:8000/api/v1'  // Electron: local backend
  : isProduction || isDockerDev
    ? '/api/v1'  // Production or Docker (port 8080): relative URL, nginx proxies /api to backend
    : (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1');  // Vite dev (port 5173)

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and UI language to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Pass user's UI language preference for LLM responses
    const uiLanguage = localStorage.getItem('ui_language') || 'en';
    config.headers['X-UI-Language'] = uiLanguage;
    
    // Debug log all outgoing requests
    console.log(`[AXIOS REQUEST] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    console.log('[AXIOS REQUEST] Full config:', {
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
      headers: config.headers,
      data: config.data,
      params: config.params
    });
    
    return config;
  },
  (error) => {
    console.error('[AXIOS REQUEST ERROR]', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to log responses
api.interceptors.response.use(
  (response) => {
    console.log(`[AXIOS RESPONSE] ${response.config.method?.toUpperCase()} ${response.config.url} - Status: ${response.status}`);
    console.log('[AXIOS RESPONSE] Data:', response.data);
    return response;
  },
  (error) => {
    console.error('[AXIOS RESPONSE ERROR]', error);
    return Promise.reject(error);
  }
);

// Error handler
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;
    const detail = axiosError.response?.data?.detail;
    
    // Handle FastAPI validation errors (422)
    if (Array.isArray(detail)) {
      return detail.map((err: any) => {
        const field = err.loc?.join('.') || 'field';
        return `${field}: ${err.msg}`;
      }).join(', ');
    }
    
    // Handle simple string errors
    if (typeof detail === 'string') {
      return detail;
    }
    
    // Fallback
    return axiosError.message || 'An error occurred';
  }
  return 'An unexpected error occurred';
};

// Projects API
export const projectsApi = {
  list: async () => {
    const response = await api.get<Project[]>('/projects');
    return response.data;
  },

  create: async (data: ProjectCreateInput) => {
    const response = await api.post<Project>('/projects', data);
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<Project>(`/projects/${id}`);
    return response.data;
  },

  update: async (id: number, data: Partial<ProjectCreateInput>) => {
    const response = await api.patch<Project>(`/projects/${id}`, data);
    return response.data;
  },

  delete: async (id: number) => {
    await api.delete(`/projects/${id}`);
  },

  getStats: async (id: number) => {
    const response = await api.get(`/projects/${id}/stats`);
    return response.data;
  },

  // Extraction prompt management
  getExtractionPrompt: async (id: number) => {
    const response = await api.get(`/projects/${id}/extraction-prompt`);
    return response.data;
  },

  updateExtractionPrompt: async (id: number, prompt: string) => {
    const response = await api.put(`/projects/${id}/extraction-prompt`, { prompt });
    return response.data;
  },

  resetExtractionPrompt: async (id: number) => {
    const response = await api.post(`/projects/${id}/extraction-prompt/reset`);
    return response.data;
  },

  testExtractionPrompt: async (id: number, data: { prompt: string; sample_text: string; model?: string }) => {
    const response = await api.post(`/projects/${id}/extraction-prompt/test`, data);
    return response.data;
  },
};

// Documents API
export const documentsApi = {
  upload: async (projectId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    // Override default Content-Type to let browser set multipart boundary
    const response = await api.post(`/projects/${projectId}/documents/upload`, formData, {
      headers: { 'Content-Type': undefined }
    });
    return response.data;
  },

  uploadMultiple: async (projectId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    // Override default Content-Type to let browser set multipart boundary
    const response = await api.post(`/projects/${projectId}/documents/upload-folder`, formData, {
      headers: { 'Content-Type': undefined }
    });
    return response.data;
  },

  list: async (projectId: number, skip = 0, limit = 500) => {
    const response = await api.get<Document[]>(`/projects/${projectId}/documents`, {
      params: { skip, limit },
    });
    return response.data;
  },

  get: async (documentId: number) => {
    const response = await api.get<Document>(`/documents/${documentId}`);
    return response.data;
  },

  delete: async (documentId: number) => {
    await api.delete(`/documents/${documentId}`);
  },

  clearAll: async (projectId: number) => {
    const response = await api.delete(`/projects/${projectId}/documents`);
    return response.data;
  },

  process: async (documentId: number, useCloud = false) => {
    const response = await api.post(`/documents/${documentId}/process`, { use_cloud: useCloud });
    return response.data;
  },

  download: async (documentId: number) => {
    const response = await api.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// Annotations API
export const annotationsApi = {
  list: async (documentId: number, annotationType?: string, skip = 0, limit = 100) => {
    const response = await api.get<Annotation[]>(`/documents/${documentId}/annotations`, {
      params: { annotation_type: annotationType, skip, limit },
    });
    return response.data;
  },

  getSummary: async (documentId: number) => {
    const response = await api.get<DocumentAnnotationsSummary>(
      `/documents/${documentId}/annotations/summary`
    );
    return response.data;
  },

  create: async (documentId: number, data: Partial<Annotation>) => {
    const response = await api.post<Annotation>(`/documents/${documentId}/annotations`, data);
    return response.data;
  },

  delete: async (annotationId: number) => {
    await api.delete(`/annotations/${annotationId}`);
  },

  search: async (projectId: number, query: string, annotationType?: string) => {
    const response = await api.get<Annotation[]>(`/projects/${projectId}/search`, {
      params: { query, annotation_type: annotationType },
    });
    return response.data;
  },
};

// Templates API
export const templatesApi = {
  list: async (useCase?: string) => {
    const response = await api.get<Template[]>('/templates', {
      params: { use_case: useCase },
    });
    return response.data;
  },

  get: async (templateId: number) => {
    const response = await api.get<Template>(`/templates/${templateId}`);
    return response.data;
  },

  create: async (data: Partial<Template>) => {
    const response = await api.post<Template>('/templates', data);
    return response.data;
  },

  validate: async (templateId: number, inputData: Record<string, any>) => {
    const response = await api.post(`/templates/${templateId}/validate`, inputData);
    return response.data;
  },
};

// Generation API
export const generationApi = {
  generate: async (projectId: number, request: GenerateDocumentRequest) => {
    const response = await api.post<GenerateDocumentResponse>('/generate', request, {
      params: { project_id: projectId },
    });
    return response.data;
  },

  getStatus: async (generationId: number) => {
    const response = await api.get<GenerateDocumentResponse>(`/generate/${generationId}/status`);
    return response.data;
  },

  download: async (generationId: number) => {
    const response = await api.get(`/generate/${generationId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  listByProject: async (projectId: number) => {
    const response = await api.get(`/generate/project/${projectId}`);
    return response.data;
  },
};

// Processing API
export const processingApi = {
  processProject: async (projectId: number, useCloud = false, forceReprocess = false, llmModel = 'ollama-8b') => {
    console.log(`[API] Making POST to /projects/${projectId}/process with use_cloud=${useCloud}, force_reprocess=${forceReprocess}, llm_model=${llmModel}`);
    // Add timestamp to bust any caching
    const response = await api.post(
      `/projects/${projectId}/process?use_cloud=${useCloud}&force_reprocess=${forceReprocess}&llm_model=${llmModel}&_t=${Date.now()}`,
      {}, // empty body
      { headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } }
    );
    console.log('[API] POST response received:', response.data);
    return response.data;
  },

  getProcessingStatus: async (projectId: number) => {
    const response = await api.get(`/projects/${projectId}/processing-status`);
    return response.data;
  },

  getCaseOverview: async (projectId: number, llmModel = 'ollama-8b') => {
    const response = await api.get(`/projects/${projectId}/case-overview?llm_model=${llmModel}`);
    return response.data;
  },

  getStats: async (projectId: number) => {
    const response = await api.get<ProcessingStats>(`/projects/${projectId}/processing-stats`);
    return response.data;
  },

  cancelDocument: async (documentId: number) => {
    await api.post(`/documents/${documentId}/cancel-processing`);
  },

  estimateTime: async (projectId: number, useCloud = false) => {
    const response = await api.post(`/projects/${projectId}/estimate-processing-time`, {
      use_cloud: useCloud,
    });
    return response.data;
  },

  getGlobalStats: async () => {
    const response = await api.get<ProcessingStats>('/processing/stats');
    return response.data;
  },
};

// Chat API
export const chatApi = {
  askV2: async (params: {
    project_id: number;
    question: string;
    mode?: 'summaries' | 'full_docs';
    model?: string;  // "claude", "openai", "ollama-14b"
  }) => {
    const response = await api.post('/chat/v2/ask', {
      project_id: params.project_id,
      question: params.question,
      mode: params.mode || 'summaries',
      model: params.model || 'claude',
    });
    return response.data;
  },
};

// Settings API
export const settingsApi = {
  get: async () => {
    const response = await api.get<AppSettings>('/settings');
    return response.data;
  },

  updateLLM: async (settings: Partial<AppSettings>) => {
    const response = await api.put('/settings/llm', settings);
    return response.data;
  },

  testConnection: async (provider: string) => {
    const response = await api.get('/settings/test-connection', {
      params: { provider },
    });
    return response.data;
  },
};

// Email Import API
export const emailApi = {
  importEmails: (projectId: number, formData: FormData) =>
    api.post(`/emails/projects/${projectId}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  
  getThreads: (projectId: number) =>
    api.get(`/emails/projects/${projectId}/threads`),
  
  getThreadMessages: (threadId: number) =>
    api.get(`/emails/threads/${threadId}/messages`),
};

// Q&A API
export const qaApi = {
  askQuestion: (projectId: number, data: { question: string }) =>
    api.post(`/qa/projects/${projectId}/ask`, data),
  
  getSuggestions: (projectId: number) =>
    api.get(`/qa/projects/${projectId}/suggestions`),
};

// Compliance API
export const complianceApi = {
  listChecklists: () =>
    api.get('/compliance/checklists'),
  
  getChecklistDetails: (checklistType: string) =>
    api.get(`/compliance/checklists/${checklistType}`),
  
  analyze: (data: { document_ids: number[]; checklist_type: string }) =>
    api.post('/compliance/analyze', data),
  
  analyzeProject: (projectId: number, checklistType: string) =>
    api.post(`/compliance/projects/${projectId}/analyze?checklist_type=${checklistType}`),
  
  exportPDF: (analysisId: number) =>
    api.get(`/compliance/export/${analysisId}/pdf`, {
      responseType: 'blob'
    }),
  
  exportExcel: (analysisId: number) =>
    api.get(`/compliance/export/${analysisId}/excel`, {
      responseType: 'blob'
    }),
};

export const comparisonApi = {
  compareDocuments: (data: {
    document_id_1: number;
    document_id_2: number;
    comparison_mode: string;
  }) => api.post('/comparison/documents', data),

  compareVersions: (data: {
    document_id: number;
    version_1: number;
    version_2: number;
    comparison_mode: string;
  }) => api.post('/comparison/versions', data),

  createVersion: (documentId: number, data: {
    version_label?: string;
    comment?: string;
  }) => {
    const params = new URLSearchParams();
    if (data.version_label) params.append('version_label', data.version_label);
    if (data.comment) params.append('comment', data.comment);
    return api.post(`/comparison/documents/${documentId}/versions?${params.toString()}`);
  },

  listVersions: (documentId: number) =>
    api.get(`/comparison/documents/${documentId}/versions`),
};

export const ocrApi = {
  process: (documentId: number, options?: {
    language?: string;
    use_textract?: boolean;
    extract_tables?: boolean;
  }) => api.post('/ocr/process', {
    document_id: documentId,
    language: options?.language || 'eng',
    use_textract: options?.use_textract || false,
    extract_tables: options?.extract_tables || false
  }),

  getStatus: (documentId: number) =>
    api.get(`/ocr/status/${documentId}`),
};

// Master Reports API
export const masterReportsApi = {
  generate: async (projectId: number, reports?: string[]) => {
    const response = await api.post('/master-reports/generate', {
      project_id: projectId,
      reports: reports || null,
    });
    return response.data;
  },

  export: async (projectId: number, reports?: string[]) => {
    const response = await api.post('/master-reports/export', {
      project_id: projectId,
      reports: reports || null,
    }, { responseType: 'blob' });
    return response;
  },

  preview: async (projectId: number, reportType: string) => {
    const response = await api.get(`/master-reports/preview/${projectId}/${reportType}`);
    return response.data;
  },
};

// Admin API
export const adminApi = {
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  getLLMStats: async (days = 30, userId?: number) => {
    const params: any = { days };
    if (userId) params.user_id = userId;
    const response = await api.get('/admin/llm-calls/stats', { params });
    return response.data;
  },

  getRAGStats: async () => {
    const response = await api.get('/admin/rag/stats');
    return response.data;
  },

  getHealth: async () => {
    const response = await api.get('/health');
    return response.data;
  },
};

// Company Profile API
export const companyProfileApi = {
  get: async () => {
    const response = await api.get('/company-profile/');
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/company-profile/', data);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.put(`/company-profile/${id}`, data);
    return response.data;
  },
  listTeam: async (profileId: number) => {
    const response = await api.get(`/company-profile/${profileId}/team`);
    return response.data;
  },
  addTeamMember: async (profileId: number, data: any) => {
    const response = await api.post(`/company-profile/${profileId}/team`, data);
    return response.data;
  },
  updateTeamMember: async (memberId: number, data: any) => {
    const response = await api.put(`/company-profile/team/${memberId}`, data);
    return response.data;
  },
  deleteTeamMember: async (memberId: number) => {
    await api.delete(`/company-profile/team/${memberId}`);
  },
  uploadLogo: async (profileId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/company-profile/${profileId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Clients API
export const clientsApi = {
  list: async () => {
    const response = await api.get('/clients/');
    return response.data;
  },
  create: async (data: any) => {
    const response = await api.post('/clients/', data);
    return response.data;
  },
  get: async (id: number) => {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },
  update: async (id: number, data: any) => {
    const response = await api.put(`/clients/${id}`, data);
    return response.data;
  },
  delete: async (id: number) => {
    await api.delete(`/clients/${id}`);
  },
};

// Proposals API
export const proposalsApi = {
  create: async (projectId: number, data: any) => {
    const response = await api.post(`/proposals/?project_id=${projectId}`, data);
    return response.data;
  },
  getByProject: async (projectId: number) => {
    const response = await api.get(`/proposals/project/${projectId}`);
    return response.data;
  },
  update: async (proposalId: number, data: any) => {
    const response = await api.put(`/proposals/${proposalId}`, data);
    return response.data;
  },
  addWorkPackage: async (proposalId: number, data: any) => {
    const response = await api.post(`/proposals/${proposalId}/work-packages`, data);
    return response.data;
  },
  updateWorkPackage: async (wpId: number, data: any) => {
    const response = await api.put(`/proposals/work-packages/${wpId}`, data);
    return response.data;
  },
  deleteWorkPackage: async (wpId: number) => {
    await api.delete(`/proposals/work-packages/${wpId}`);
  },
  generateAll: async (proposalId: number, sectionTypes?: string[]) => {
    const response = await api.post(`/proposals/${proposalId}/generate`, 
      sectionTypes ? { section_types: sectionTypes } : null
    );
    return response.data;
  },
  generateSection: async (proposalId: number, sectionType: string) => {
    const response = await api.post(`/proposals/${proposalId}/generate-section`, {
      section_type: sectionType,
    });
    return response.data;
  },
  updateSection: async (sectionId: number, data: any) => {
    const response = await api.put(`/proposals/sections/${sectionId}`, data);
    return response.data;
  },
  approveSection: async (sectionId: number) => {
    const response = await api.put(`/proposals/sections/${sectionId}/approve`);
    return response.data;
  },
};

export const billingApi = {
  getPlans: () => api.get('/billing/plans'),
  
  createCheckout: (data: { tier: string }) =>
    api.post('/billing/checkout', data),
  
  createPortal: () => api.get('/billing/portal'),
  
  getSubscription: () => api.get('/billing/subscription'),
};

export default api
